import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import "./actionModal.scss";

export interface IAction {
  icon: IconProp;
  onClick: () => void;
  label: string;
  disabled: boolean;
}

interface IActionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  actionGroups: IAction[][];
}

export default function ActionModal(props: IActionModalProps) {
  const { open, setOpen, actionGroups } = props;
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= TABLET_WIDTH,
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= TABLET_WIDTH);
    }

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    document.addEventListener("mousedown", handleOutsideModalClick);
    document.addEventListener("touchstart", handleOutsideModalClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideModalClick);
      document.removeEventListener("touchstart", handleOutsideModalClick);
    };
  }, [isMobile]);

  function handleOutsideModalClick(event: any) {
    if (!modalRef.current || modalRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  }

  function handleOnClick(onClick: () => void) {
    ReactDOM.unstable_batchedUpdates(() => {
      onClick();
      setOpen(false);
    });
  }

  function renderSheetActions() {
    return (
      <div className="action-modal-sheet">
        {actionGroups?.map((actions, groupIndex) => {
          return (
            <div key={groupIndex} className="action-modal-sheet-group">
              {actions?.map((action, actionIndex) => {
                return (
                  <button
                    key={actionIndex}
                    type="button"
                    className="action-modal-sheet-action"
                    onClick={() => handleOnClick(action.onClick)}
                    disabled={action.disabled}
                  >
                    <FontAwesomeIcon className="action-modal-sheet-action-icon" icon={action.icon} />
                    <span className="action-modal-sheet-action-label">{action.label}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet
        open={open}
        title={t("Actions")}
        size="small"
        height="flexible"
        animate
        closable
        footer={null}
        onCancel={() => setOpen(false)}
        backDropCancel
      >
        {renderSheetActions()}
      </Sheet>
    );
  }

  return (
    <Portal isMounted={open}>
      <div ref={modalRef} className="action-modal">
        {actionGroups?.map((actions, index) => {
          return (
            <div key={index} className="action-modal-action-group">
              {actions?.map((action, index) => {
                return (
                  <button
                    key={index}
                    type="button"
                    className="action-modal-action-group-action"
                    onClick={() => handleOnClick(action.onClick)}
                    disabled={action.disabled}
                  >
                    <FontAwesomeIcon className="action-modal-action-group-action-icon" icon={action.icon} />
                    <p className="action-modal-action-group-action-label">{action.label}</p>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      <div className="action-modal-backdrop" onClick={() => setOpen(false)} />
    </Portal>
  );
}
